// Editor's Desk — chat with Claude Opus, with tools that can search arcs,
// fetch articles, draft new pieces, and publish them.
//
// Wraps POST /news/editor/desk/chat (multi-turn). On each send we POST the
// full transcript so far. Server returns `{ role: "assistant", content,
// tool_actions, usage }` — we append the assistant message and surface the
// tool actions as small banners ("Created draft: …" → links to article
// review) so the editor can see what Claude just did.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { Textarea } from "../../components/ui/Input";
import { api } from "../../lib/api";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ToolAction {
  success?: boolean;
  tool?: string;
  article_id?: string;
  article_title?: string;
  sub_thread_id?: string;
  sub_thread_name?: string;
  message?: string;
  [key: string]: any;
}

interface ChatResponse {
  role: "assistant";
  content: string;
  tool_actions: ToolAction[];
  usage: { input_tokens?: number; output_tokens?: number };
}

export default function EditorsDeskPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  // Tool actions are keyed by the index of the assistant message they
  // belong to so we can render the right receipts under each turn.
  const [toolActionsByIndex, setToolActionsByIndex] = useState<
    Record<number, ToolAction[]>
  >({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const userMsg: Message = { role: "user", content: trimmed };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    setError(null);

    try {
      const r = await api<ChatResponse>("/news/editor/desk/chat", {
        method: "POST",
        body: { messages: nextMessages },
      });
      const assistantMsg: Message = {
        role: "assistant",
        content: r.content,
      };
      const updated = [...nextMessages, assistantMsg];
      setMessages(updated);
      setToolActionsByIndex((prev) => ({
        ...prev,
        [updated.length - 1]: r.tool_actions ?? [],
      }));
    } catch (e: any) {
      setError(e.message ?? "Failed to send");
      // Remove the optimistic user message so the next attempt re-sends it
      setMessages(messages);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSend();
    }
  }

  function clearChat() {
    if (
      messages.length > 0 &&
      !confirm("Clear the conversation?")
    ) {
      return;
    }
    setMessages([]);
    setToolActionsByIndex({});
    setError(null);
  }

  return (
    <div style={styles.outer}>
      <header style={styles.header}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
            Editor's Desk
          </h1>
          <CardSubtle>
            Multi-turn conversation with Claude. Cmd/Ctrl+Enter to send.
          </CardSubtle>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={clearChat}
          disabled={messages.length === 0}
        >
          Clear
        </Button>
      </header>

      <div ref={scrollRef} style={styles.transcript}>
        {messages.length === 0 ? (
          <Card>
            <CardTitle>Start a conversation</CardTitle>
            <CardSubtle>
              Try: "Find me the latest source articles about Sudan and draft an
              article connecting them to the Egypt arc."
            </CardSubtle>
          </Card>
        ) : (
          messages.map((m, i) => (
            <div key={i} style={m.role === "user" ? styles.userTurn : styles.assistantTurn}>
              <div style={styles.roleLabel}>
                {m.role === "user" ? "You" : "Claude"}
              </div>
              <div style={m.role === "user" ? styles.userBubble : styles.assistantBubble}>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.content}</div>
              </div>
              {m.role === "assistant" &&
                toolActionsByIndex[i]?.map((action, idx) => (
                  <ToolActionReceipt key={idx} action={action} />
                ))}
            </div>
          ))
        )}
        {sending && (
          <div style={styles.assistantTurn}>
            <div style={styles.roleLabel}>Claude</div>
            <div style={styles.assistantBubble}>
              <Spinner size={14} /> Thinking…
            </div>
          </div>
        )}
        {error && (
          <div style={styles.errorBox}>{error}</div>
        )}
      </div>

      <div style={styles.composer}>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Claude — research a story, draft an article, classify a sub-thread…"
          rows={3}
          style={{ flex: 1 }}
        />
        <Button onClick={handleSend} disabled={sending || !input.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
}

function ToolActionReceipt({ action }: { action: ToolAction }) {
  // Render a small banner describing what Claude did. Article-related
  // actions link to the review screen so the editor can pick up where
  // Claude left off.
  const tool = action.tool ?? "";
  let label = action.message;

  if (action.article_id) {
    return (
      <div style={styles.toolReceipt}>
        <span>{labelForTool(tool, action)}</span>
        <Link
          to={`/article/${action.article_id}`}
          style={{ marginLeft: 8, color: "var(--accent)" }}
        >
          Open in review →
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.toolReceipt}>
      {label ?? labelForTool(tool, action)}
    </div>
  );
}

function labelForTool(tool: string, action: ToolAction): string {
  switch (tool) {
    case "create_draft_article":
      return `Created draft: "${action.article_title ?? "untitled"}"`;
    case "publish_article":
      return `Published: "${action.article_title ?? action.article_id ?? "article"}"`;
    case "create_sub_thread":
      return `Created sub-thread: "${action.sub_thread_name ?? "new thread"}"`;
    case "search_arcs_and_threads":
      return `Searched arcs / sub-threads`;
    case "get_recent_articles":
      return `Fetched recent articles`;
    case "get_event_groups":
      return `Fetched event groups`;
    default:
      return tool ? `Used tool: ${tool}` : "Tool action";
  }
}

const styles: Record<string, React.CSSProperties> = {
  outer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 120px)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
  },
  transcript: {
    flex: 1,
    overflowY: "auto",
    padding: "0 4px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  userTurn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
  },
  assistantTurn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
  },
  roleLabel: {
    fontSize: 11,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  userBubble: {
    background: "var(--accent)",
    color: "#0e0e0f",
    padding: "10px 14px",
    borderRadius: "var(--radius-md)",
    maxWidth: "70%",
    fontSize: 14,
    lineHeight: 1.5,
  },
  assistantBubble: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    color: "var(--text-primary)",
    padding: "10px 14px",
    borderRadius: "var(--radius-md)",
    maxWidth: "85%",
    fontSize: 14,
    lineHeight: 1.6,
  },
  toolReceipt: {
    background: "rgba(245, 158, 11, 0.08)",
    border: "1px solid rgba(245, 158, 11, 0.3)",
    color: "var(--text-secondary)",
    padding: "6px 12px",
    borderRadius: "var(--radius-sm)",
    fontSize: 12,
    marginTop: 4,
  },
  errorBox: {
    background: "var(--danger)",
    color: "#fff",
    padding: 10,
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
  },
  composer: {
    display: "flex",
    gap: 8,
    paddingTop: 12,
    borderTop: "1px solid var(--border)",
    alignItems: "flex-end",
  },
};
